<script setup>
const props = defineProps(['product'])

import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { useShopifyStore } from '@stores/shopify.js'
import Button from '@/components/global/Button.vue'
import Tag from '@/components/global/Tag.vue'
import ArrowLeft from '@/components/icons/ArrowLeft.vue'
import ChevronRight from '@/components/icons/ChevronRight.vue'
import Link from '@/components/icons/Link.vue'
import Tick from '@/components/icons/Tick.vue'
import Plus from '@/components/icons/Plus.vue'
import Minus from '@/components/icons/Minus.vue'
import ChevronUp from '@/components/icons/ChevronUp.vue'
import TransitionExpand from '@/components/transitions/TransitionExpand.vue'
import Lightbox from '@/components/global/Lightbox.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import gsap from 'gsap'

const shopify = useShopifyStore()
const copied = ref(false)
const benefitsExpanded = ref(true)
const quantity = ref(1)
const selectedOptions = ref({})
const addingToCart = ref(false)
const lightboxOpen = ref(false)
const lightboxIndex = ref(0)
const cursorRef = ref(null)
const cursorVisible = ref(false)
let xTo, yTo

onMounted(() => {
	if (props.product.options) {
		props.product.options.forEach((option) => {
			if (option.values && option.values.length > 0) {
				selectedOptions.value[option.name] = option.values[0]
			}
		})
	}

	xTo = gsap.quickTo(cursorRef.value, 'x', {
		duration: 0.08,
		ease: 'power1.out',
	})
	yTo = gsap.quickTo(cursorRef.value, 'y', {
		duration: 0.08,
		ease: 'power1.out',
	})
})

const selectedVariant = computed(() => {
	if (!props.product.variants) return null

	return props.product.variants.find((variant) => {
		return variant.selectedOptions.every((option) => selectedOptions.value[option.name] === option.value)
	})
})

const displayTitle = computed(() => {
	const variantTitle = selectedVariant.value?.title
	if (!variantTitle || variantTitle === 'Default Title') return props.product.title
	return `${props.product.title} - ${variantTitle}`
})

const getSwatchForOption = (optionName, optionValue) => {
	if (!props.product.variants) return null

	const variant = props.product.variants.find((variant) => {
		return variant.selectedOptions.every((option) => {
			if (option.name === optionName) {
				return option.value === optionValue
			}
			return selectedOptions.value[option.name] === option.value
		})
	})

	return variant?.swatch || null
}

const addToCart = async () => {
	if (!selectedVariant.value || !selectedVariant.value.available) {
		return
	}

	addingToCart.value = true

	try {
		await shopify.addToCart({
			id: selectedVariant.value.id,
			quantity: quantity.value,
		})
	} catch (error) {
		console.error('Failed to add to cart:', error)
	} finally {
		addingToCart.value = false
	}
}

const copyLink = () => {
	navigator.clipboard.writeText(window.location.href)
	copied.value = true
	setTimeout(() => {
		copied.value = false
	}, 2000)
}

const onGalleryMouseMove = (e) => {
	xTo(e.clientX)
	yTo(e.clientY)
}

const onGalleryMouseEnter = (e) => {
	cursorVisible.value = true
	gsap.set(cursorRef.value, { x: e.clientX, y: e.clientY })
}

const onGalleryMouseLeave = () => {
	cursorVisible.value = false
}

const openLightbox = (index) => {
	lightboxIndex.value = index
	lightboxOpen.value = true
}

const closeLightbox = () => {
	lightboxOpen.value = false
}

watch(lightboxOpen, (newVal) => {
	if (newVal) {
		document.body.style.overflow = 'hidden'
	} else {
		document.body.style.overflow = ''
	}
})

onUnmounted(() => {
	document.body.style.overflow = ''
})
</script>

<template>
	<div class="container pb-48">
		<div class="h8 font-primary-condensed mb-32 hidden items-center gap-16 lg:flex">
			<Button
				:to="{
					name: 'teams',
				}"
				class="-theme-background max-h-28! min-h-28! p-8!">
				<ArrowLeft />
				<p>See all teams</p>
			</Button>
			<div class="flex items-center gap-8">
				<router-link
					class="flex items-center gap-4 transition-opacity hover:opacity-50"
					:to="{
						name: 'collection',
						params: { slug: product.parent_collection.slug },
					}">
					<p>{{ product.parent_collection.title }}</p>
				</router-link>
				<div class="flex items-center gap-8">
					<ChevronRight class="size-16" />
					<p>{{ product.title }}</p>
				</div>
			</div>
		</div>
		<div class="grid grid-cols-12 items-start gap-y-16 lg:gap-x-32 lg:gap-y-0">
			<div class="col-span-12 grid grid-cols-2 gap-8 lg:col-span-6 xl:col-span-7" @mouseenter="onGalleryMouseEnter" @mouseleave="onGalleryMouseLeave" @mousemove="onGalleryMouseMove">
				<div class="col-span-2 mb-8 flex flex-col gap-16 lg:hidden">
					<div class="flex flex-wrap items-center justify-between lg:hidden">
						<div class="flex items-center gap-8" v-if="product.parent_collection">
							<img class="h-16" v-if="product.parent_collection.icon" :src="product.parent_collection.icon" :alt="product.parent_collection.icon_alt || ''" />
							<p class="h8 font-primary-condensed rounded-lg bg-(--theme-surface)/10 uppercase">
								{{ product.parent_collection.title }}
							</p>
						</div>
						<button class="h8 font-primary-condensed flex items-center gap-8 opacity-50 transition-opacity hover:opacity-100" :class="{ 'opacity-100': copied }" @click="copyLink">
							<p>{{ copied ? 'Copied!' : 'Share' }}</p>
							<Link v-if="!copied" class="size-16" />
							<Tick v-else class="size-16" />
						</button>
					</div>
					<div class="flex flex-col gap-8 lg:hidden">
						<h1 class="md:h3 lg:h4 h5 font-primary-condensed">
							{{ displayTitle }}
						</h1>
						<div class="h8 font-primary-condensed flex flex-wrap items-center gap-8">
							<Button
								:to="{
									name: 'teams',
								}"
								class="-theme-background max-h-28! min-h-28! p-8!">
								<ArrowLeft class="size-10" />
								<p>See all teams</p>
							</Button>
							<div class="flex items-center gap-4">
								<router-link
									class="flex items-center gap-4 transition-opacity hover:opacity-50"
									:to="{
										name: 'collection',
										params: { slug: product.parent_collection.slug },
									}">
									<p>{{ product.parent_collection.title }}</p>
								</router-link>
								<div class="flex items-center gap-4">
									<ChevronRight class="size-16" />
									<p>{{ product.title }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
				<template v-for="(image, index) in selectedVariant?.gallery ?? product.images" :key="index">
					<template v-if="index === 0">
						<img class="col-span-2 aspect-video w-full cursor-pointer rounded-lg object-cover lg:cursor-none" :src="image.url" @click="openLightbox(index)" :alt="image.alt || `${product.title} image ${index + 1}`" />
						<div class="col-span-2 flex items-center gap-12 rounded-lg bg-(--theme-surface)/10 p-16">
							<div class="text-14 flex size-24 shrink-0 items-center justify-center rounded-full border border-current leading-none opacity-70">i</div>
							<p class="h7 font-primary-condensed leading-tight">Reflections shown on the lenses are from studio lighting and are not part of the product.</p>
						</div>
					</template>
					<img
						v-else
						class="aspect-square w-full cursor-pointer rounded-lg object-cover lg:cursor-none"
						:class="{
							'col-span-2 aspect-video': index % 3 === 0,
						}"
						:src="image.url"
						@click="openLightbox(index)"
						:alt="image.alt || `${product.title} image ${index + 1}`" />
				</template>

				<!-- <div
					v-if="product.images.length > 1"
					class="col-span-2 w-full lg:hidden"
				>
					<Swiper class="overflow-hidden rounded-lg">
						<SwiperSlide
							v-for="(image, index) in selectedVariant?.gallery ??
							product.images"
							:key="index"
						>
							<img
								class="aspect-square w-full cursor-pointer object-cover transition-transform"
								:src="image.url"
								:alt="image.alt || `${product.title} image ${index + 1}`"
								@click="openLightbox(index)"
							/>
						</SwiperSlide>
					</Swiper>
				</div> -->
			</div>
			<div class="col-span-12 flex flex-col items-start gap-32 lg:col-span-6 lg:gap-40 xl:col-span-5">
				<div class="flex w-full flex-col gap-24">
					<div class="hidden flex-wrap items-center justify-between lg:flex">
						<div class="flex items-center gap-20" v-if="product.parent_collection">
							<img class="h-24 md:h-40" v-if="product.parent_collection.icon" :src="product.parent_collection.icon" :alt="product.parent_collection.icon_alt || product.parent_collection.title" />
						</div>
						<button class="h8 font-primary-condensed flex items-center gap-8 opacity-50 transition-opacity hover:opacity-100" :class="{ 'opacity-100': copied }" @click="copyLink">
							<p>{{ copied ? 'Copied!' : 'Share' }}</p>
							<Link v-if="!copied" class="size-16" />
							<Tick v-else class="size-16" />
						</button>
					</div>
					<div class="flex max-w-400 flex-col gap-32">
						<h1 class="md:h3 lg:h2 h5 font-primary-condensed hidden lg:block">
							{{ displayTitle }}
						</h1>
						<div class="text-text-muted body-md space-y-[1ch]" v-if="product.description">
							<p>
								{{ product.description }}
							</p>
						</div>
					</div>
				</div>

				<ul class="flex flex-col gap-32">
					<li class="flex w-full flex-col gap-16" v-for="(option, index) in product.options" :key="index">
						<p class="h8 font-primary-condensed">
							{{ option.name }}
						</p>
						<div v-if="option.name === 'Style' || option.name === 'Lens Designs'" class="flex flex-wrap gap-12">
							<button
								v-for="value in option.values"
								:key="value"
								class="size-64 rounded-xl border-2 bg-(--theme-surface)/10 p-4 transition-all hover:bg-(--theme-surface)/20 lg:size-74"
								:class="{
									'border-(--theme-background)': selectedOptions[option.name] === value,
									'border-transparent': selectedOptions[option.name] !== value,
								}"
								@click="selectedOptions[option.name] = value">
								<img v-if="getSwatchForOption(option.name, value)" :src="getSwatchForOption(option.name, value)" :alt="value" class="size-full rounded-lg object-cover" />
							</button>
						</div>

						<div v-else class="flex flex-wrap gap-12">
							<button
								v-for="value in option.values"
								:key="value"
								class="rounded-lg border-2 px-16 py-8 text-sm transition-all"
								:class="{
									'border-(--theme-background) text-(--theme-background)': selectedOptions[option.name] === value,
									'border-(--theme-surface)/20': selectedOptions[option.name] !== value,
								}"
								@click="selectedOptions[option.name] = value">
								{{ value }}
							</button>
						</div>
					</li>
				</ul>

				<div class="w-full" v-if="product.benefits.length > 0">
					<button class="flex w-full items-center justify-between" @click="benefitsExpanded = !benefitsExpanded">
						<h2 class="h8 font-primary-condensed">Benefits</h2>
						<div class="shrink-0 transition-transform duration-500" :class="{ 'size-16 rotate-180': benefitsExpanded }">
							<ChevronUp />
						</div>
					</button>
					<TransitionExpand>
						<div v-if="benefitsExpanded && product.benefits.length > 0">
							<div class="pt-20">
								<ul class="grid gap-x-40 gap-y-16 text-sm sm:grid-cols-2">
									<li class="flex items-center gap-16" v-for="benefit in product.benefits" :key="benefit">
										<Tick class="size-12" />
										<p>{{ benefit }}</p>
									</li>
								</ul>
							</div>
						</div>
					</TransitionExpand>
				</div>
				<div class="flex w-full flex-col items-end gap-10 p-12">
					<Tag v-if="product.tag" :label="product.tag" :hex="product.tag_color" />

					<div class="flex w-full flex-col gap-y-16 rounded-lg bg-(--theme-surface)/10 lg:flex-row lg:items-center lg:justify-between lg:gap-x-64">
						<div class="flex flex-col gap-4">
							<p class="text-sm line-through opacity-50" v-if="selectedVariant?.compare_at_price">
								{{ selectedVariant.compare_at_price }}
							</p>
							<p class="h6 font-primary">
								{{ selectedVariant?.price || product.price }}
							</p>
						</div>

						<div class="grid w-full grid-cols-2 gap-8 lg:flex lg:w-auto lg:gap-12">
							<div class="bg-bg-base flex rounded-lg p-2">
								<button class="transition-alls flex size-48 items-center justify-center rounded-lg not-disabled:hover:bg-white/5 disabled:opacity-50" @click="quantity > 1 && quantity--" :disabled="quantity === 1" aria-label="Decrease quantity">
									<Minus class="size-16" />
								</button>
								<div class="flex h-48 w-40 grow items-center justify-center lg:grow-0">
									<span class="h8 font-primary-condensed">
										{{ quantity }}
									</span>
								</div>
								<button class="flex size-48 items-center justify-center rounded-lg transition-colors hover:bg-white/5" @click="quantity++" aria-label="Increase quantity">
									<Plus class="size-16" />
								</button>
							</div>
							<Button
								:disabled="!selectedVariant?.available || addingToCart"
								:class="[
									'-theme-background',
									{
										'cursor-not-allowed opacity-50': !selectedVariant?.available,
									},
								]"
								@click="addToCart">
								{{ !selectedVariant?.available ? 'Out of Stock' : addingToCart ? 'Adding...' : product.add_to_cart_label || 'Add to cart' }}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div ref="cursorRef" class="pointer-events-none fixed top-0 left-0 z-100 hidden transition-opacity lg:block" :class="{ 'opacity-0': !cursorVisible || lightboxOpen }">
			<div class="-translate-x-1/2 -translate-y-1/2">
				<Plus class="size-32" />
			</div>
		</div>

		<Lightbox :images="selectedVariant?.gallery ?? product.images" :initial-index="lightboxIndex" :is-open="lightboxOpen" @close="closeLightbox" />
	</div>
</template>
